import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Button,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal,
  Show
} from "shared/components";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import type {
  InstructionTarget,
  InstructionType
} from "types/landlord/address";
import {
  getPropertyName,
  getTargetSource,
  getUnitName,
  INSTRUCTION_TYPE_LABELS,
  unitLabel,
  type PropertyDictionary
} from "./helpers";

const buildSummaryParts = ({
  selectedTypes,
  overwriteCountByType,
  countPrefix,
  dictionary
}: {
  selectedTypes: InstructionType[];
  overwriteCountByType: Record<InstructionType, number>;
  countPrefix: string;
  dictionary: PropertyDictionary;
}) =>
  selectedTypes
    .filter((type) => overwriteCountByType[type] > 0)
    .map(
      (type) =>
        `${countPrefix}${overwriteCountByType[type]} ${unitLabel(overwriteCountByType[type], dictionary)} already have their own ${INSTRUCTION_TYPE_LABELS[type].toLowerCase()}`
    );

const OverwriteTargetRow = ({
  target,
  selectedTypes,
  dictionary
}: {
  target: InstructionTarget;
  selectedTypes: InstructionType[];
  dictionary: PropertyDictionary;
}) => {
  const overriddenLabels = selectedTypes
    .filter((type) => getTargetSource(target, type) === "custom")
    .map((type) => INSTRUCTION_TYPE_LABELS[type].toLowerCase());

  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-content-700">
          {getUnitName(target, dictionary)}
        </p>
        <p className="truncate text-sm text-content-300">
          {getPropertyName(target, dictionary)}
        </p>
      </div>
      <p className="shrink-0 text-xs font-bold text-warning-700">
        {`Own ${overriddenLabels.join(" and ")} will be replaced`}
      </p>
    </div>
  );
};

export const OverwriteUnitInstructionsDialog = ({
  opened,
  onClose,
  onConfirm,
  sourceName,
  selectedTypes,
  selectedCount,
  overwriteTargets,
  isAllMode,
  allModeOverwriteCountByType,
  isPending,
  canClose
}: {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  sourceName: string;
  selectedTypes: InstructionType[];
  selectedCount: number;
  overwriteTargets: InstructionTarget[];
  isAllMode: boolean;
  allModeOverwriteCountByType: Record<InstructionType, number>;
  isPending: boolean;
  canClose: boolean;
}) => {
  const dictionary = useUnitsPropertiesDictionary();

  const explicitOverwriteCountByType = selectedTypes.reduce(
    (acc, type) => ({
      ...acc,
      [type]: overwriteTargets.filter(
        (target) => getTargetSource(target, type) === "custom"
      ).length
    }),
    {} as Record<InstructionType, number>
  );

  const summaryParts = buildSummaryParts({
    selectedTypes,
    overwriteCountByType: isAllMode
      ? allModeOverwriteCountByType
      : explicitOverwriteCountByType,
    countPrefix: isAllMode ? "up to " : "",
    dictionary
  });

  return (
    <Modal
      onClose={onClose}
      opened={opened}
      panelClassName="max-w-[720px] w-full"
    >
      <header className="flex items-start justify-between gap-4 border-b border-alpha-40 px-6 py-6">
        <h3 className="text-xl font-bold text-content-1000">
          Replace existing instructions?
        </h3>
        <IconButton
          HugeIcon={Cancel01Icon}
          disabled={!canClose}
          onClick={onClose}
          size={IconButtonSize.BASE}
          variant={IconButtonVariant.TEXT_BASE}
        />
      </header>

      <div className="border-b border-alpha-40 px-6 py-4">
        <p className="rounded-lg bg-warning-50 p-4 text-sm text-warning-800">
          {isAllMode ? (
            <>Of the {dictionary.units} matching your search</>
          ) : (
            <>
              Of the {selectedCount} {unitLabel(selectedCount, dictionary)} you
              selected
            </>
          )}
          : <span className="font-bold">{summaryParts.join("; ")}</span>.
          They&apos;ll be replaced with the instructions saved on {sourceName}.
        </p>
      </div>

      <Show if={!isAllMode}>
        <div className="max-h-[240px] overflow-y-auto border-b border-alpha-40 px-6 py-4">
          {overwriteTargets.map((target) => (
            <OverwriteTargetRow
              dictionary={dictionary}
              key={target.id}
              selectedTypes={selectedTypes}
              target={target}
            />
          ))}
        </div>
      </Show>

      <footer className="flex items-center justify-end gap-2 px-6 py-4">
        <Button disabled={!canClose} onClick={onClose}>
          Back
        </Button>
        <Button
          loading={isPending}
          onClick={onConfirm}
          variant={ButtonVariant.PRIMARY}
        >
          Replace and apply
        </Button>
      </footer>
    </Modal>
  );
};
